/**
 * Frontend half of the dark-deploy switchboard (design §3b).
 *
 * Streams `config/modules` and answers "is this module on for this user in
 * this org?" with the same pure resolver the backend mirrors. Until the first
 * snapshot arrives the answer comes from the production-surface fallback —
 * which is why the route guard awaits `ready()` before judging a route, so a
 * dev/stage user is not bounced by prod's surface during the first paint.
 *
 * The listener opens only while someone is signed in, and again at every sign-in: one opened on
 * the sign-in page is refused by the rules and terminated for good, which left everyone who
 * signed in that way on the fallback (no Issues, no bell) until they reloaded (28 Oct).
 */
import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { BehaviorSubject, EMPTY, Observable, Subscription, firstValueFrom, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { ModuleContext, ModuleKey, ModulesDoc, PROD_SURFACE_FALLBACK, isModuleOn, moduleOrgs } from '@newgenus/common';

export const MODULES_DOC_PATH = 'config/modules';
/** How long the guard waits for the first snapshot before judging by the fallback. */
export const MODULES_READY_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class ModuleSwitchService implements OnDestroy {
    private readonly doc$ = new BehaviorSubject<ModulesDoc | null>(null);
    private readonly ready$ = new BehaviorSubject<boolean>(false);
    private sub?: Subscription;

    constructor(
        private afs: AngularFirestore,
        afAuth: AngularFireAuth
    ) {
        this.sub = afAuth.authState
            .pipe(
                map((user) => !!user),
                distinctUntilChanged(),
                switchMap((signedIn) => {
                    if (!signedIn) {
                        // Nothing behind the sign-in page asks; answer from the fallback at once.
                        return of<ModulesDoc | null | undefined>(null);
                    }
                    // A fresh sign-in waits for its own snapshot.
                    this.ready$.next(false);
                    return this.afs
                        .doc<ModulesDoc>(MODULES_DOC_PATH)
                        .valueChanges()
                        .pipe(
                            catchError((err: unknown) => {
                                console.warn(
                                    '[moduleSwitch] cannot read config/modules — using the production-surface fallback',
                                    err
                                );
                                this.doc$.next(null);
                                this.ready$.next(true);
                                return EMPTY;
                            })
                        );
                })
            )
            .subscribe((d) => {
                this.doc$.next(d && typeof d.version === 'number' && d.modules ? d : null);
                this.ready$.next(true);
            });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    /** The document in effect (fallback when none). */
    get document(): ModulesDoc {
        return this.doc$.value ?? PROD_SURFACE_FALLBACK;
    }

    /** Resolves once the first snapshot (or error) has arrived, or after the timeout — never rejects. */
    ready(): Promise<void> {
        if (this.ready$.value) return Promise.resolve();
        return firstValueFrom(
            new Observable<void>((sub) => {
                const a = this.ready$.pipe(filter(Boolean), take(1)).subscribe(() => {
                    sub.next();
                    sub.complete();
                });
                const b = timer(MODULES_READY_TIMEOUT_MS).subscribe(() => {
                    sub.next();
                    sub.complete();
                });
                return () => {
                    a.unsubscribe();
                    b.unsubscribe();
                };
            })
        );
    }

    /** Synchronous answer from the latest snapshot. */
    isOn(module: ModuleKey, ctx: ModuleContext = {}): boolean {
        return isModuleOn(this.doc$.value, module, ctx);
    }

    /** Live answer; emits on every snapshot that changes the verdict. */
    isOn$(module: ModuleKey, ctx: ModuleContext = {}): Observable<boolean> {
        return this.doc$.pipe(
            map((d) => isModuleOn(d, module, ctx)),
            distinctUntilChanged()
        );
    }

    orgs(module: ModuleKey): string[] {
        return moduleOrgs(this.doc$.value, module);
    }

    /**
     * Emits the document version in effect (0 = fallback), once per change. Menus are memoised
     * per version, so whatever builds one must rebuild when this moves - a flip made while the
     * app is open would otherwise leave a dark module's entry in the sidebar until the next
     * user-doc emission (found by the switchboard e2e, e2e/modules).
     */
    get version$(): Observable<number> {
        return this.doc$.pipe(
            map((d) => d?.version ?? 0),
            distinctUntilChanged()
        );
    }

    /** The document version in effect (0 = fallback). */
    get version(): number {
        return this.document.version;
    }
}
